/**
 * Sovereign Ash: Nareth Protocol
 * Renderer Capability Detection and Selection
 *
 * Detects WebGPU availability, falls back to WebGL2,
 * and classifies device tier for quality presets.
 */

export type RendererBackend = 'webgpu' | 'webgl2';
export type DeviceTier = 'ultra' | 'high' | 'medium' | 'low';

export interface RendererCapability {
  /** Selected rendering backend */
  backend: RendererBackend;
  /** Maximum texture dimension supported */
  maxTextureSize: number;
  /** Whether compute shaders are available (WebGPU only) */
  supportsCompute: boolean;
  /** Whether timestamp queries are available */
  supportsTimestampQuery: boolean;
  /** Maximum color attachments for MRT */
  maxColorAttachments: number;
  /** Classified device performance tier */
  deviceTier: DeviceTier;
  /** Human-readable GPU adapter name */
  adapterName: string;
  /** Whether the fallback was used */
  isFallback: boolean;
  /** Warning message if fallback was used */
  fallbackReason?: string;
}

export interface WebGPULimits {
  maxTextureDimension2D: number;
  maxBindGroups: number;
  maxColorAttachments: number;
  maxBufferSize: number;
}

/**
 * Detect the best available renderer and classify device capabilities.
 * Attempts WebGPU first, falls back to WebGL2.
 */
export async function detectRenderer(): Promise<RendererCapability> {
  // Try WebGPU first
  const webgpuResult = await tryWebGPU();
  if (webgpuResult) {
    return webgpuResult;
  }

  // Fall back to WebGL2
  const webglResult = tryWebGL2();
  if (webglResult) {
    return webglResult;
  }

  // No suitable renderer found
  throw new Error(
    'No suitable WebGL2 or WebGPU renderer available. ' +
    'Please use a modern browser with hardware acceleration enabled.'
  );
}

/**
 * Attempt to initialize WebGPU and query capabilities.
 */
async function tryWebGPU(): Promise<RendererCapability | null> {
  if (typeof navigator === 'undefined' || !('gpu' in navigator)) {
    return null;
  }

  try {
    const gpu = navigator.gpu as GPU;
    const adapter = await gpu.requestAdapter({
      powerPreference: 'high-performance',
    });

    if (!adapter) {
      return null;
    }

    // Request device to query limits
    const device = await adapter.requestDevice();
    const limits = device.limits;

    // Get adapter info for display
    const info = await getAdapterInfo(adapter);

    // Classify tier based on limits
    const tier = classifyWebGPUTier(limits);

    // Clean up test device
    device.destroy();

    return {
      backend: 'webgpu',
      maxTextureSize: limits.maxTextureDimension2D,
      supportsCompute: true,
      supportsTimestampQuery: adapter.features.has('timestamp-query'),
      maxColorAttachments: limits.maxColorAttachments,
      deviceTier: tier,
      adapterName: info,
      isFallback: false,
    };
  } catch (error) {
    console.warn('WebGPU initialization failed:', error);
    return null;
  }
}

/**
 * Get human-readable adapter name.
 */
async function getAdapterInfo(adapter: GPUAdapter): Promise<string> {
  try {
    if ('requestAdapterInfo' in adapter) {
      const info = await (adapter as GPUAdapter & {
        requestAdapterInfo: () => Promise<GPUAdapterInfo>;
      }).requestAdapterInfo();
      return info.description || info.vendor || 'Unknown GPU';
    }
  } catch {
    // Ignore errors
  }
  return 'WebGPU Adapter';
}

/**
 * Classify WebGPU device tier based on limits.
 */
function classifyWebGPUTier(limits: GPUSupportedLimits): DeviceTier {
  const maxTexture = limits.maxTextureDimension2D;
  const maxBuffer = limits.maxBufferSize;

  // Ultra: High-end discrete GPU
  if (maxTexture >= 16384 && maxBuffer >= 268435456) {
    return 'ultra';
  }

  // High: Mid-range discrete or high-end integrated
  if (maxTexture >= 8192 && maxBuffer >= 134217728) {
    return 'high';
  }

  // Medium: Standard integrated graphics
  if (maxTexture >= 4096) {
    return 'medium';
  }

  // Low: Basic capability
  return 'low';
}

/**
 * Attempt to initialize WebGL2 and query capabilities.
 */
function tryWebGL2(): RendererCapability | null {
  if (typeof document === 'undefined') {
    return null;
  }

  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2', {
      powerPreference: 'high-performance',
      failIfMajorPerformanceCaveat: true,
    });

    if (!gl) {
      return null;
    }

    // Query capabilities
    const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
    const maxColorAttachments = gl.getParameter(gl.MAX_COLOR_ATTACHMENTS) as number;

    // Get renderer info
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    const adapterName = debugInfo
      ? (gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string)
      : 'WebGL2 Renderer';

    // Classify tier (WebGL2 is generally lower tier)
    const tier = classifyWebGL2Tier(maxTextureSize);

    // Clean up
    const loseContext = gl.getExtension('WEBGL_lose_context');
    loseContext?.loseContext();

    return {
      backend: 'webgl2',
      maxTextureSize,
      supportsCompute: false,
      supportsTimestampQuery: false,
      maxColorAttachments,
      deviceTier: tier,
      adapterName,
      isFallback: true,
      fallbackReason: 'WebGPU not available. Using WebGL2 with reduced features.',
    };
  } catch (error) {
    console.warn('WebGL2 initialization failed:', error);
    return null;
  }
}

/**
 * Classify WebGL2 device tier based on capabilities.
 */
function classifyWebGL2Tier(maxTextureSize: number): DeviceTier {
  // WebGL2 caps at medium tier (no compute, limited features)
  if (maxTextureSize >= 16384) {
    return 'medium'; // Cap at medium for WebGL2
  }
  if (maxTextureSize >= 8192) {
    return 'medium';
  }
  return 'low';
}

/**
 * Get recommended quality preset based on device tier.
 */
export function getRecommendedPreset(tier: DeviceTier): {
  resolution: number;
  shadows: 'off' | 'low' | 'medium' | 'high' | 'ultra';
  reflections: boolean;
  volumetrics: boolean;
  antialiasing: 'off' | 'fxaa' | 'taa';
  particleDensity: number;
} {
  switch (tier) {
    case 'ultra':
      return {
        resolution: 100,
        shadows: 'ultra',
        reflections: true,
        volumetrics: true,
        antialiasing: 'taa',
        particleDensity: 1.0,
      };
    case 'high':
      return {
        resolution: 100,
        shadows: 'high',
        reflections: true,
        volumetrics: false,
        antialiasing: 'taa',
        particleDensity: 0.75,
      };
    case 'medium':
      return {
        resolution: 75,
        shadows: 'medium',
        reflections: false,
        volumetrics: false,
        antialiasing: 'fxaa',
        particleDensity: 0.5,
      };
    case 'low':
      return {
        resolution: 50,
        shadows: 'low',
        reflections: false,
        volumetrics: false,
        antialiasing: 'fxaa',
        particleDensity: 0.25,
      };
  }
}
